"use client";

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTheme } from "next-themes";
import { Button } from "@/components/ui/button";
import { SPOTLIGHT_CHARS_PER_SECOND } from "@/features/candidate/lib/profile-constants";
import { Icon } from "@/components/ui/icon";
import { ProfileSummarize } from "./summarize";
import { useSpotlight } from "@/hooks/use-spotlights";
import { useProfile } from "../context";

function SpotlightContent({ spotlight }: { spotlight: string }) {
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (hasAnimated) return;
    const timer = setTimeout(
      () => setHasAnimated(true),
      (spotlight.length / SPOTLIGHT_CHARS_PER_SECOND) * 1000 + 200,
    );
    return () => clearTimeout(timer);
  }, [spotlight, hasAnimated]);

  const segments = useMemo(() => parseSpotlightMarkdown(spotlight), [spotlight]);

  if (!hasAnimated) {
    return <SpotlightTypewriter segments={segments} />;
  }
  return (
    <span>
      {segments.map((seg, i) =>
        seg.highlighted ? (
          <SpotlightHighlight key={i}>{seg.text}</SpotlightHighlight>
        ) : (
          <span key={i}>{seg.text}</span>
        ),
      )}
    </span>
  );
}

export interface SpotlightSegment {
  text: string;
  highlighted: boolean;
}

export function parseSpotlightMarkdown(raw: string): SpotlightSegment[] {
  const segments: SpotlightSegment[] = [];
  const regex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(raw)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        text: raw.slice(lastIndex, match.index),
        highlighted: false,
      });
    }
    segments.push({ text: match[1], highlighted: true });
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < raw.length) {
    segments.push({ text: raw.slice(lastIndex), highlighted: false });
  }
  if (segments.length === 0) {
    segments.push({ text: raw, highlighted: false });
  }
  return segments;
}

export function SpotlightHighlight({ children }: { children: ReactNode }) {
  return (
    <span className="bg-[rgba(124,58,237,0.1)] dark:bg-[rgba(229,215,255,0.13)] rounded-sm px-0.5 py-[0.5px] text-foreground">
      {children}
    </span>
  );
}

export function SpotlightShimmer({
  label = "Summarizing profile",
}: { label?: string } = {}) {
  return (
    <span className="text-sm font-normal leading-normal spotlight-shimmer-text">
      {label}
    </span>
  );
}

export function SpotlightTypewriter({
  segments,
}: {
  segments: SpotlightSegment[];
}) {
  const fullText = segments.map((s) => s.text).join("");
  const [charCount, setCharCount] = useState(0);
  const requestRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);
  const charsPerSecond = SPOTLIGHT_CHARS_PER_SECOND;

  useEffect(() => {
    startTimeRef.current = performance.now();
    const animate = (now: number) => {
      const elapsed = now - startTimeRef.current;
      const target = Math.min(
        Math.floor((elapsed / 1000) * charsPerSecond),
        fullText.length,
      );
      setCharCount(target);
      if (target < fullText.length) {
        requestRef.current = requestAnimationFrame(animate);
      }
    };
    requestRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef.current);
  }, [fullText]);

  const segmentOffsets = useMemo(() => {
    const offsets: number[] = [];
    let acc = 0;
    for (const seg of segments) {
      offsets.push(acc);
      acc += seg.text.length;
    }
    return offsets;
  }, [segments]);

  return (
    <span>
      {segments.map((seg, i) => {
        const segStart = segmentOffsets[i];
        const visible = Math.max(
          0,
          Math.min(seg.text.length, charCount - segStart),
        );
        if (visible === 0) return null;
        const slice = seg.text.slice(0, visible);
        return seg.highlighted ? (
          <SpotlightHighlight key={i}>{slice}</SpotlightHighlight>
        ) : (
          <span key={i}>{slice}</span>
        );
      })}
      {charCount < fullText.length && (
        <span className="inline-block w-0.5 h-3.5 bg-[#7c3aed] dark:bg-[#c9a0ff] animate-pulse align-middle ml-px" />
      )}
    </span>
  );
}

export function AISpotlightPanel({
  profileId: profileIdProp,
  searchId,
  spotlight: spotlightProp,
  spotlightLoading: spotlightLoadingProp,
}: {
  /** Override the context profile id (e.g. the public share page). */
  profileId?: string | null;
  /** Search the profile was opened from; tailors the spotlight to it (and
   *  shares the result card's cache entry). Omit for the generic spotlight. */
  searchId?: string | null;
  /** Pre-fetched spotlight text; when provided, skips the `useSpotlight` fetch
   *  result (used by surfaces that can't call the authed spotlight endpoint). */
  spotlight?: string;
  spotlightLoading?: boolean;
} = {}) {
  const { profileId: ctxProfileId } = useProfile();
  const profileId = profileIdProp !== undefined ? profileIdProp : ctxProfileId;
  const [summarizeOpen, setSummarizeOpen] = useState(false);
  const { resolvedTheme } = useTheme();
  const isLight = resolvedTheme === "light";

  const fetched = useSpotlight(profileId, searchId);
  const spotlight =
    spotlightProp !== undefined ? spotlightProp : fetched.spotlight;
  const spotlightLoading =
    spotlightLoadingProp !== undefined
      ? spotlightLoadingProp
      : fetched.isLoading;

  if (!profileId) return null;
  if (!spotlight && !spotlightLoading) return null;

  return (
    <div
      className="flex flex-col gap-2.5 items-start px-4 sm:px-8 py-4 sm:py-6 w-full border-b border-border"
      style={{
        backgroundImage: isLight
          ? "linear-gradient(180deg, rgba(124,58,237,0) 0%, rgba(124,58,237,0.06) 100%)"
          : "linear-gradient(180deg, rgba(154,106,237,0) 0%, rgba(154,106,237,0.1) 100%)",
      }}
    >
      <div className="flex items-center justify-between gap-2 py-1.5 w-full">
        <p className="text-lg font-medium text-foreground leading-normal truncate min-w-0">
          AI-Powered Spotlight
        </p>
        <Button
          type="button"
          variant="linear"
          size="sm"
          onClick={() => setSummarizeOpen(true)}
          className="shrink-0 before:bg-linear-to-r before:from-(--gradient-color) before:via-white before:to-(--gradient-color) text-black border-black/25"
          style={{ "--gradient-color": "#c8b3f0" } as React.CSSProperties}
        >
          <span className="relative z-10 inline-flex items-center gap-[inherit]">
            <Icon name="IconSparklesTwo" size={13} />
            Summarize
          </span>
        </Button>
      </div>
      <div className="w-full text-sm font-normal text-foreground/70 leading-normal">
        <AnimatePresence mode="wait">
          {spotlightLoading && !spotlight ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <SpotlightShimmer />
            </motion.div>
          ) : spotlight ? (
            <motion.div
              key="content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <SpotlightContent spotlight={spotlight} />
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
      <ProfileSummarize
        profileId={profileId ?? ""}
        open={summarizeOpen}
        onOpenChange={setSummarizeOpen}
      />
    </div>
  );
}
